import type {CreateFlagArgs} from "@atlaskit/flag";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {getMediaLibraryDirent, getMediaLibraryDirents, uploadMediaLibraryFile} from "~/modules/mediaLibrary/mediaLibraryApi";
import {buildMediaLibraryNeptuneIdUrl} from "~/modules/mediaLibrary/mediaLibraryUrls";

export type ResolvedImage = {
    id: Uuid;
    imagePath: string;
    /** Original media-library file name when available (preferred over UUID URL segment). */
    fileName?: string;
    createdAt?: string;
    updatedAt?: string;
};

const UUID_FILE_SEGMENT_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Display label for a resolved media-library image.
 * Prefer dirent `fileName`; never show the UUID from `/media/id/{uuid}` URLs.
 */
export function getResolvedImageDisplayName(image: Pick<ResolvedImage, "fileName" | "imagePath">): string {
    if (image.fileName !== undefined && image.fileName.trim() !== "") {
        return image.fileName.trim();
    }

    const segment = image.imagePath.split("/").pop() ?? "";
    let decoded = segment;
    try {
        decoded = decodeURIComponent(segment);
    } catch {
        decoded = segment;
    }

    if (decoded !== "" && UUID_FILE_SEGMENT_PATTERN.test(decoded) !== true) {
        return decoded;
    }

    return "Image";
}

/**
 * Public gateway URL for a media-library dirent.
 * When accessToken + intellsysBaseUrl are provided, also loads the dirent name.
 */
export async function resolveImageByIdFromApi({
    accessToken,
    createdAt,
    direntId,
    fileName: explicitFileName,
    intellsysBaseUrl,
    updatedAt,
    workspaceId,
}: {
    direntId: Uuid;
    fileName?: string;
    createdAt?: string;
    updatedAt?: string;
    accessToken?: string;
    intellsysBaseUrl?: string;
    workspaceId: Uuid;
}): Promise<ResolvedImage> {
    let fileName = explicitFileName;
    if (
        (fileName === undefined || fileName.trim() === "")
        && accessToken !== undefined
        && accessToken !== ""
        && intellsysBaseUrl !== undefined
        && intellsysBaseUrl !== ""
    ) {
        const direntResult = await getMediaLibraryDirent({
            accessToken: accessToken,
            direntId: direntId,
            intellsysBaseUrl: intellsysBaseUrl,
            workspaceId: workspaceId,
        });
        if (direntResult.success === true && direntResult.data !== null) {
            fileName = direntResult.data.name;
        }
    }

    return {
        id: direntId,
        fileName: fileName,
        imagePath: buildMediaLibraryNeptuneIdUrl({
            direntId: direntId,
            intellsysBaseUrl: intellsysBaseUrl ?? import.meta.env.INTELLSYS_BASE_URL,
            workspaceId: workspaceId,
        }),
        createdAt: createdAt,
        updatedAt: updatedAt,
    };
}

const MEDIA_LIBRARY_GET_DIRENTS_MAX_IDS = 200;

/**
 * Resolve media-library dirent IDs to display images.
 * When auth is available, metadata is fetched in batch via `/media-library/get-dirents`.
 */
export async function resolveImageIdsFromApi({
    accessToken,
    imageIds,
    intellsysBaseUrl,
    workspaceId,
}: {
    imageIds: Uuid[];
    accessToken?: string;
    intellsysBaseUrl?: string;
    workspaceId: Uuid;
}): Promise<ResolvedImage[]> {
    if (imageIds.length === 0) {
        return [];
    }

    const uniqueImageIds: Array<Uuid> = [];
    const seenImageIds = new Set<string>();
    for (const imageId of imageIds) {
        if (seenImageIds.has(imageId)) {
            continue;
        }
        seenImageIds.add(imageId);
        uniqueImageIds.push(imageId);
    }

    const metadataById = new Map<string, {fileName: string; createdAt: string; updatedAt: string}>();

    if (
        accessToken !== undefined
        && accessToken !== ""
        && intellsysBaseUrl !== undefined
        && intellsysBaseUrl !== ""
    ) {
        for (let offset = 0; offset < uniqueImageIds.length; offset += MEDIA_LIBRARY_GET_DIRENTS_MAX_IDS) {
            const chunk = uniqueImageIds.slice(offset, offset + MEDIA_LIBRARY_GET_DIRENTS_MAX_IDS);
            const direntsResult = await getMediaLibraryDirents({
                accessToken: accessToken,
                direntIds: chunk,
                intellsysBaseUrl: intellsysBaseUrl,
                workspaceId: workspaceId,
            });
            if (direntsResult.success !== true) {
                continue;
            }
            for (const dirent of direntsResult.data) {
                metadataById.set(dirent.id, {
                    fileName: dirent.name,
                    createdAt: dirent.createdAt,
                    updatedAt: dirent.updatedAt,
                });
            }
        }
    }

    return Promise.all(
        imageIds.map(async (imageId) => {
            const metadata = metadataById.get(imageId);
            return resolveImageByIdFromApi({
                createdAt: metadata?.createdAt,
                direntId: imageId,
                fileName: metadata?.fileName,
                intellsysBaseUrl: intellsysBaseUrl,
                updatedAt: metadata?.updatedAt,
                workspaceId: workspaceId,
            });
        }),
    );
}

export async function uploadImageToApi({
    file,
    accessToken,
    intellsysBaseUrl,
    workspaceId,
    parentFolderId,
    showFlag,
}: {
    file: File;
    accessToken: string;
    intellsysBaseUrl: string;
    workspaceId: Uuid;
    parentFolderId: Uuid;
    showFlag: (flag: CreateFlagArgs) => void;
}): Promise<ResolvedImage> {
    const uploadResult = await uploadMediaLibraryFile({
        accessToken,
        file,
        parentFolderId,
        intellsysBaseUrl,
        workspaceId,
    });

    if (uploadResult.success !== true) {
        console.error(uploadResult.error);
        showFlag({
            title: "Failed to upload file",
            description: uploadResult.error.message,
            appearance: "error",
            isAutoDismiss: true,
        });
        throw uploadResult.error;
    }

    return resolveImageByIdFromApi({
        createdAt: uploadResult.data.createdAt,
        direntId: uploadResult.data.id,
        fileName: uploadResult.data.name,
        updatedAt: uploadResult.data.updatedAt,
        workspaceId: workspaceId,
    });
}
